import { IconLayer, TextLayer, ScatterplotLayer } from '@deck.gl/layers';
import Supercluster from 'supercluster';
import { buildDemoVesselIconLayers } from '../../utils/demoVesselMapIcons';
import {
  vesselDotRadiusPixelsForMapZoom,
  vesselIconPixelSizeForMapZoom,
  vesselThreatIconPixelSizeForMapZoom
} from '../../utils/vesselMarkerZoomScale';

// Custom Chevron SVG for direction
// Pointing UP (0 degrees) by default
const CHEVRON_SVG = `
<svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
  <path d="M32 8 L56 56 L32 44 L8 56 Z" fill="white" stroke="black" stroke-width="2"/>
</svg>
`;

const CHEVRON_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(CHEVRON_SVG)}`;

const ICON_MAPPING = {
    marker: { x: 0, y: 0, width: 64, height: 64, mask: true }
};

/**
 * Creates layer instances for marker clustering
 * @param {Array} data - Array of vessel points
 * @param {Object} options - Configuration options
 * @returns {Array} Deck.gl layers
 */
export const generateMarkerClusterLayer = (data, options = {}) => {
    const {
        id = 'marker-cluster-layer',
        visible = true,
        zoom,
        bounds, // [west, south, east, north]
        onHover,
        onClick,
        // Optional: use vessel icons for single points (iconAtlas, iconMapping, getIcon, getColor)
        iconAtlas: vesselIconAtlas,
        iconMapping: vesselIconMapping,
        getIcon: getVesselIcon,
        getColor: getVesselColor,
        // Optional: draw single points as color blobs (circles) instead of icons; use with getColor
        useDotMarkers = false,
        /** When set, single points use demo raster vessel icons instead of dots/chevron. */
        demoRasterVesselIcons = null // { sizePixels, threatSizePixels?, getKind }
    } = options;

    if (!data || data.length === 0) return [];

    // 1. Initialize Supercluster
    const index = new Supercluster({
        radius: 40,
        maxZoom: 16
    });

    // 2. Prepare points for Supercluster (GeoJSON Feature format)
    const points = data.map(d => ({
        type: 'Feature',
        properties: {
            cluster: false,
            ...d,
            threat_detected: d.threat_detected === 'Yes' // Store as boolean for easier reduction
        },
        geometry: {
            type: 'Point',
            coordinates: [d.lon, d.lat]
        }
    }));

    index.load(points);

    // 3. Get clusters for current bbox and zoom
    // If bounds are not available (e.g. initial load), use whole world
    const bbox = bounds || [-180, -85, 180, 85];
    const zm = Math.floor(zoom || 0);
    const zoomFloat = typeof zoom === 'number' && !Number.isNaN(zoom) ? zoom : zm;
    const clusters = index.getClusters(bbox, zm);



    // 4. Seperate clusters and single points
    const clusterPoints = [];
    const singlePoints = [];

    clusters.forEach(cluster => {
        const { cluster: isCluster } = cluster.properties;
        const [longitude, latitude] = cluster.geometry.coordinates;

        if (isCluster) {
            // Check if any point in cluster has threat
            // Supercluster doesn't auto-reduce custom props unless configured with map/reduce
            // Since we didn't configure map/reduce in constructor (to keep it simple for now),
            // we might miss "threat inside cluster" status unless we use supercluster's map/reduce options.
            // Let's re-configure supercluster index or use a simpler approach.
            //
            // Re-configuring Supercluster for threat detection:
            // We need to instantiate the index properly outside this function if we want performance,
            // but for React/Deck.gl stateless usage, we re-create or memoize.
            //
            // To properly support "threat inside cluster", we need to traverse leaves or use map/reduce.
            // Let's use the leaves method if count is small, OR better: configure Supercluster properly.
            //
            // However, `generateMarkerClusterLayer` is called comfortably in render cycle?
            // Re-creating index every frame is expensive (O(n)).
            // IDEALLY this logic belongs in a useMemo hook in the parent component.
            //
            // For now, let's assume valid performance or lightweight data.
            // To get threat status:
            const leaves = index.getLeaves(cluster.id, Infinity);
            const hasThreat = leaves.some(l => l.properties.threat_detected);

            clusterPoints.push({
                ...cluster.properties,
                longitude,
                latitude,
                hasThreat
            });
        } else {
            singlePoints.push({
                ...cluster.properties,
                longitude,
                latitude
            });
        }
    });

    // 5. Create Layers

    // A. Cluster Circle Layer (Scatterplot)
    const clusterLayer = new ScatterplotLayer({
        id: `${id}-clusters`,
        data: clusterPoints,
        pickable: true,
        stroked: true,
        filled: true,
        radiusScale: 1,
        radiusMinPixels: 20,
        radiusMaxPixels: 30,
        lineWidthMinPixels: 2,
        getPosition: d => [d.longitude, d.latitude],
        getFillColor: d => d.hasThreat ? [239, 68, 68, 200] : [100, 149, 237, 200], // Red tint if threat, else blue
        getLineColor: [255, 255, 255],
        // Expand on click
        onClick: info => {
            if (onClick && info.object) {
                const expansionZoom = index.getClusterExpansionZoom(info.object.cluster_id);
                // Callback to flyTo? We need to expose this.
                // For now just pass the event
                onClick({
                    ...info,
                    expansionZoom: expansionZoom,
                    isCluster: true
                })
            }
        },
        visible
    });

    // B. Cluster Count Text Layer
    const clusterTextLayer = new TextLayer({
        id: `${id}-cluster-text`,
        data: clusterPoints,
        pickable: false,
        getPosition: d => [d.longitude, d.latitude],
        getText: d => d.point_count.toString(),
        getSize: 16,
        getColor: [255, 255, 255],
        visible
    });

    // C. Single points: demo raster icons, color blobs (dots), icon atlas, or chevron
    const useVesselIcons = !useDotMarkers && vesselIconAtlas && vesselIconMapping && getVesselIcon && getVesselColor;

    // Dots must win over demo raster — otherwise useDotMarkers is ignored whenever demoRasterVesselIcons is passed.
    if (useDotMarkers && getVesselColor) {
        const dotR = vesselDotRadiusPixelsForMapZoom(zoomFloat);
        const dotsLayer = new ScatterplotLayer({
            id: `${id}-dots`,
            data: singlePoints,
            pickable: true,
            filled: true,
            stroked: true,
            radiusUnits: 'pixels',
            radiusScale: 1,
            getRadius: dotR,
            getPosition: d => [d.longitude, d.latitude],
            getFillColor: d => {
                const c = getVesselColor(d);
                return Array.isArray(c) && c.length >= 4 ? c : [...(c || [34, 197, 94]), 230];
            },
            getLineColor: [255, 255, 255],
            lineWidthMinPixels: 1,
            onHover,
            onClick,
            visible
        });
        return [clusterLayer, clusterTextLayer, dotsLayer];
    }

    if (
        demoRasterVesselIcons &&
        typeof demoRasterVesselIcons.getKind === 'function' &&
        singlePoints.length > 0
    ) {
        const { sizePixels: sizePixelsOpt, threatSizePixels: threatSizePixelsOpt, getKind } = demoRasterVesselIcons;
        const sizePixels = sizePixelsOpt ?? vesselIconPixelSizeForMapZoom(zoomFloat);
        const threatSizePixels = threatSizePixelsOpt ?? vesselThreatIconPixelSizeForMapZoom(zoomFloat);
        const threatData = [];
        const defensiveData = [];
        const auvData = [];
        const friendlyData = [];
        for (const p of singlePoints) {
            const k = getKind(p);
            if (k === 'threat') threatData.push(p);
            else if (k === 'defensive') defensiveData.push(p);
            else if (k === 'auv') auvData.push(p);
            else friendlyData.push(p);
        }
        const demoLayers = buildDemoVesselIconLayers({
            idPrefix: `${id}-demo`,
            threatData,
            defensiveData,
            auvData,
            friendlyData,
            getPosition: (d) => [d.longitude, d.latitude],
            sizePixels,
            threatSizePixels,
            pickable: true,
            onHover,
            onClick
        });
        return [clusterLayer, clusterTextLayer, ...demoLayers];
    }

    const iconPx = vesselIconPixelSizeForMapZoom(zoomFloat);
    const iconLayer = new IconLayer({
        id: `${id}-icons`,
        data: singlePoints,
        pickable: true,
        iconAtlas: useVesselIcons ? vesselIconAtlas : CHEVRON_ICON_URL,
        iconMapping: useVesselIcons ? vesselIconMapping : ICON_MAPPING,
        getIcon: useVesselIcons ? d => getVesselIcon(d) || 'ship' : () => 'marker',
        getPosition: d => [d.longitude, d.latitude],
        getAngle: 0,
        getSize: useVesselIcons
            ? iconPx
            : d => {
                const db = d.radar_reflectivity_db || 0;
                const minDb = 0; const maxDb = 50;
                const clamped = Math.max(minDb, Math.min(db, maxDb));
                const normalized = (clamped - minDb) / (maxDb - minDb);
                const lo = iconPx * 0.85;
                const hi = iconPx * 1.35;
                return lo + normalized * (hi - lo);
            },
        getColor: useVesselIcons ? d => getVesselColor(d) : d => d.threat_detected ? [239, 68, 68] : [0, 200, 80],
        sizeMinPixels: useVesselIcons ? Math.max(10, Math.round(iconPx * 0.5)) : undefined,
        sizeMaxPixels: useVesselIcons ? Math.round(iconPx * 1.35) : undefined,
        onHover,
        onClick,
        visible
    });

    return [clusterLayer, clusterTextLayer, iconLayer];
};
